import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  Bot,
  Image as ImageIcon,
  Rss,
  Send,
  Workflow,
  Layers,
  Newspaper,
  ExternalLink,
} from "lucide-react";

import { db } from "@/lib/billing";
import { SeoHead } from "@/components/seo/SeoHead";
import { Section } from "@/components/marketing/Section";
import { Eyebrow } from "@/components/marketing/Eyebrow";
import { DisplayHeading } from "@/components/marketing/DisplayHeading";
import { Rule } from "@/components/marketing/Rule";
import { FeatureTriad } from "@/components/marketing/FeatureTriad";
import { CTABanner } from "@/components/marketing/CTABanner";
import { LogoStrip } from "@/components/marketing/LogoStrip";
import { SiteFrame } from "@/components/marketing/SiteFrame";

import {
  buildOrganizationJsonLd,
  buildWebSiteJsonLd,
  buildItemListJsonLd,
  buildProductJsonLd,
} from "@/lib/seo";
import { useCountUp } from "@/hooks/useCountUp";
import { usePublicStats } from "@/hooks/usePublicStats";
import { FEATURED_SITES, SHOWCASE_SITES, siteUrl } from "@/lib/showcase-sites";
import { RESOURCE_CATEGORIES } from "@/lib/marketing-categories";
import {
  PLAN_AUDIENCE,
  PLAN_DISPLAY_NAMES,
  type PlanSlug,
  locText,
} from "@/lib/marketing-plan-matrix";
import { cn } from "@/lib/utils";
import { useLocale, intlLocale, pickLocalized, pickDbText } from "@/lib/locale";
import { formatMoney, planMonthlyCents, planSetupCents } from "@/lib/currency";

/** Same three plans as the /tarifs cards, same source of truth. */
const TEASER_PLANS: PlanSlug[] = ["starter", "growth", "network"];
const POPULAR_PLAN: PlanSlug = "growth";

interface ResourceRow {
  slug: string;
  category: string;
  title_fr: string | null;
  title_en: string | null;
  title_es?: string | null;
  title_ar?: string | null;
  reading_minutes: number | null;
}

/** Real published slugs, used only if the marketing_articles query fails. */
const FALLBACK_RESOURCES: ResourceRow[] = [
  {
    slug: "automatiser-site-actualite-guide-2026",
    category: "guides",
    title_fr: "Automatiser un site d'actualité : guide complet 2026",
    title_en: "Automating a news site: the complete 2026 guide",
    reading_minutes: 11,
  },
  {
    slug: "cout-reel-article-ia",
    category: "guides",
    title_fr: "Le coût réel d'un article généré par IA",
    title_en: "The real cost of an AI-written article",
    reading_minutes: 7,
  },
  {
    slug: "delapresse-vs-chatgpt-pipeline-complet",
    category: "comparatifs",
    title_fr: "delapresse vs ChatGPT : pipeline complet ou simple rédacteur",
    title_en: "delapresse vs ChatGPT: a full pipeline or a single writer",
    reading_minutes: 8,
  },
];

async function fetchTeaserResources(): Promise<ResourceRow[]> {
  const { data, error } = await db
    .from("marketing_articles")
    .select("slug, category, title_fr, title_en, title_es, title_ar, reading_minutes")
    .eq("is_published", true)
    .order("is_featured", { ascending: false })
    .order("sort_order", { ascending: true })
    .limit(3);
  if (error || !data || data.length < 3) return FALLBACK_RESOURCES;
  return data as ResourceRow[];
}

export default function HomePage() {
  const { t, i18n } = useTranslation();
  const lang = useLocale();
  const locale = intlLocale(lang);

  const stats = usePublicStats();
  const sites = useCountUp(stats.data?.sites_count ?? SHOWCASE_SITES.length);
  const arts = useCountUp(stats.data?.articles_count ?? 0);
  const models = useCountUp(stats.data?.models_count ?? 27);

  const { data: resources = FALLBACK_RESOURCES } = useQuery({
    queryKey: ["marketing-home-resources"],
    queryFn: fetchTeaserResources,
    staleTime: 10 * 60 * 1000,
    retry: 1,
    throwOnError: false,
    placeholderData: FALLBACK_RESOURCES,
  });

  const today = new Intl.DateTimeFormat(locale, {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  }).format(new Date());

  const productListJsonLd = buildItemListJsonLd(TEASER_PLANS, (slug) =>
    buildProductJsonLd({
      name: locText(PLAN_DISPLAY_NAMES[slug], lang),
      description: locText(PLAN_AUDIENCE[slug], lang),
      offers: {
        price: (planMonthlyCents(slug) ?? 0) / 100,
        priceCurrency: "EUR",
        url: `/tarifs#${slug}`,
      },
    }),
  );

  const [heroA, heroB, heroC] = FEATURED_SITES;
  const homeGrid = FEATURED_SITES.slice(0, 6);

  const categoryLabel = (slug: string) => {
    const c = RESOURCE_CATEGORIES.find((x) => x.slug === slug);
    return c ? t(`marketing.resources.categories.${c.i18n}.label`) : slug;
  };

  return (
    <>
      <SeoHead
        title={t("marketing.home.seo.title")}
        description={t("marketing.home.seo.description")}
        path="/"
        locale={lang}
        jsonLd={[buildOrganizationJsonLd(), buildWebSiteJsonLd(), productListJsonLd]}
      />

      {/* A — Hero. Text on the left, a collage of three production sites on
          the right. No customer content is fetched here: the collage is
          static screenshots of sites that run on delapresse. */}
      <section className="relative overflow-hidden border-b border-rule bg-background">
        <div aria-hidden className="pointer-events-none absolute inset-0">
          <div
            className="aurora-blob"
            style={{
              width: 720,
              height: 720,
              top: -180,
              right: -120,
              background: "radial-gradient(circle, hsl(var(--accent-press) / 0.45) 0%, transparent 60%)",
              animation: "aurora-drift 18s ease-in-out infinite",
            }}
          />
          <div
            className="aurora-blob"
            style={{
              width: 560,
              height: 560,
              bottom: -160,
              left: -120,
              background: "radial-gradient(circle, hsl(var(--primary) / 0.3) 0%, transparent 60%)",
              animation: "aurora-drift-2 22s ease-in-out infinite",
            }}
          />
        </div>
        <div aria-hidden className="grid-overlay" />
        <div aria-hidden className="grain-overlay" />

        <div className="relative mx-auto grid max-w-[1200px] grid-cols-1 items-center gap-12 px-5 py-16 md:px-6 md:py-20 lg:min-h-[80vh] lg:grid-cols-[1.05fr_1fr] lg:py-24">
          <div className="max-w-2xl">
            <Eyebrow>{t("marketing.home.hero.eyebrow", { date: today })}</Eyebrow>
            <DisplayHeading level={1} className="mt-5 text-[clamp(2.75rem,5.5vw,5rem)] leading-[1.02]">
              {t("marketing.home.hero.title")}
            </DisplayHeading>
            <p className="mt-6 max-w-xl text-lg leading-relaxed text-muted-foreground md:text-xl">
              {t("marketing.home.hero.lead")}
            </p>

            <div className="mt-8 flex flex-wrap items-center gap-3">
              <Link
                to="/dpadmin?intent=signup"
                className="group relative inline-flex h-12 items-center gap-2 overflow-hidden bg-accent-press px-6 text-sm font-semibold uppercase tracking-wider text-accent-press-foreground transition-all hover:shadow-[0_20px_50px_-15px_hsl(var(--accent-press)/0.7)]"
              >
                <span
                  aria-hidden
                  className="absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-white/30 to-transparent transition-transform duration-700 ease-out group-hover:translate-x-full"
                />
                <span className="relative">{t("marketing.home.hero.ctaPrimary")}</span>
                <ArrowRight className="relative h-4 w-4 transition-transform group-hover:translate-x-0.5" />
              </Link>
              <Link
                to="/vitrine"
                className="inline-flex h-12 items-center border border-rule bg-background/60 px-6 text-sm font-semibold uppercase tracking-wider text-foreground backdrop-blur-sm transition-colors hover:bg-foreground hover:text-background"
              >
                {t("marketing.home.hero.ctaSecondary")}
              </Link>
            </div>

            <Rule className="mt-10" />

            <div className="mt-6 grid grid-cols-3 gap-6 sm:gap-10">
              <div>
                <p className="text-display text-3xl tabular-nums gradient-text md:text-4xl">{sites.toLocaleString(locale)}</p>
                <p className="dateline mt-1 text-[10px]">{t("marketing.home.hero.ticker.sites")}</p>
              </div>
              <div>
                <p className="text-display text-3xl tabular-nums gradient-text md:text-4xl">{arts.toLocaleString(locale)}</p>
                <p className="dateline mt-1 text-[10px]">{t("marketing.home.hero.ticker.articles")}</p>
              </div>
              <div>
                <p className="text-display text-3xl tabular-nums gradient-text md:text-4xl">{models.toLocaleString(locale)}</p>
                <p className="dateline mt-1 text-[10px]">{t("marketing.home.hero.ticker.models")}</p>
              </div>
            </div>
          </div>

          {/* Desktop collage: three production sites, fanned. */}
          <div className="relative hidden w-full lg:block" style={{ minHeight: 480 }}>
            <div
              aria-hidden
              className="absolute inset-0 -m-8 rounded-full opacity-60"
              style={{
                background: "radial-gradient(circle, hsl(var(--accent-press) / 0.28) 0%, transparent 65%)",
                animation: "glow-pulse 6s ease-in-out infinite",
              }}
            />
            <div className="absolute left-0 top-0 w-[68%] rotate-[-3deg]" style={{ animation: "float-y 9s ease-in-out infinite" }}>
              <SiteFrame site={heroB} size="sm" />
            </div>
            <div
              className="absolute right-0 top-10 w-[68%] rotate-[2.5deg]"
              style={{ animation: "float-y 11s ease-in-out infinite", animationDelay: "1.2s" }}
            >
              <SiteFrame site={heroC} size="sm" />
            </div>
            <div className="absolute inset-x-[6%] bottom-0 w-[88%]">
              <SiteFrame site={heroA} eager className="shadow-editorial-lg" />
            </div>
          </div>

          {/* Mobile strip: same three sites, horizontal scroll. */}
          <div className="-mx-5 flex snap-x gap-4 overflow-x-auto px-5 pb-2 lg:hidden">
            {FEATURED_SITES.slice(0, 3).map((s, i) => (
              <div key={s.domain} className="w-[78%] flex-none snap-start sm:w-[55%]">
                <SiteFrame site={s} size="sm" eager={i === 0} />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* B — Trust strip: the publications that run on delapresse. */}
      <Section padding="sm" tone="paper">
        <LogoStrip
          label={t("marketing.home.trust.eyebrow")}
          items={SHOWCASE_SITES.map((s) => ({
            name: s.name,
            render: (
              <a href={siteUrl(s)} target="_blank" rel="noopener noreferrer" className="hover:text-accent-press">
                {s.name}
              </a>
            ),
          }))}
        />
      </Section>

      <Rule />

      {/* C — Pipeline in four movements */}
      <Section padding="md">
        <div className="max-w-3xl">
          <Eyebrow>{t("marketing.home.pipeline.eyebrow")}</Eyebrow>
          <DisplayHeading level={2} className="mt-4">
            {t("marketing.home.pipeline.title")}
          </DisplayHeading>
        </div>
        <div className="relative mt-12 grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
          <div
            aria-hidden
            className="pointer-events-none absolute left-[6%] right-[6%] top-[60px] hidden h-px lg:block"
            style={{
              background:
                "repeating-linear-gradient(to right, hsl(var(--rule)) 0 6px, transparent 6px 14px)",
            }}
          />
          {[
            { icon: Rss, n: "01", k: "sources" },
            { icon: Bot, n: "02", k: "generate" },
            { icon: ImageIcon, n: "03", k: "image" },
            { icon: Send, n: "04", k: "publish" },
          ].map((step) => {
            const Icon = step.icon;
            return (
              <article key={step.n} className="lift-card relative border border-rule bg-background p-7 md:p-8">
                <div className="dateline">{t("marketing.home.pipeline.stepLabel", { n: step.n })}</div>
                <div className="relative mt-4 inline-flex h-12 w-12 items-center justify-center rounded-full bg-accent-press/10 ring-1 ring-accent-press/30">
                  <Icon className="relative h-5 w-5 text-accent-press" strokeWidth={1.75} aria-hidden />
                </div>
                <h3 className="mt-4 text-display text-xl leading-tight">{t(`marketing.home.pipeline.steps.${step.k}.title`)}</h3>
                <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{t(`marketing.home.pipeline.steps.${step.k}.body`)}</p>
              </article>
            );
          })}
        </div>
        <div className="mt-10">
          <Link to="/comment-ca-marche" className="inline-flex items-center gap-2 text-sm font-medium text-foreground hover:text-accent-press">
            {t("marketing.home.pipeline.seeMore")} <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </Section>

      <Rule />

      {/* D — Sites in production */}
      <Section padding="md" tone="paper">
        <div className="flex flex-col items-start gap-6 md:flex-row md:items-end md:justify-between">
          <div className="max-w-2xl">
            <Eyebrow>{t("marketing.home.sites.eyebrow")}</Eyebrow>
            <DisplayHeading level={2} className="mt-4">{t("marketing.home.sites.title")}</DisplayHeading>
            <p className="mt-5 text-base leading-relaxed text-muted-foreground">{t("marketing.home.sites.lead")}</p>
          </div>
          <Link to="/vitrine" className="inline-flex items-center gap-2 text-sm font-medium text-foreground hover:text-accent-press">
            {t("marketing.home.sites.seeAll", { count: SHOWCASE_SITES.length })} <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
        <div className="mt-10 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {homeGrid.map((s) => (
            <article key={s.domain} className="lift-card flex flex-col border border-rule bg-background">
              <SiteFrame site={s} size="sm" className="border-0 shadow-none" />
              <div className="flex items-start justify-between gap-3 border-t border-rule p-4">
                <div className="min-w-0">
                  <h3 className="truncate text-display text-lg leading-tight">{s.name}</h3>
                  <p className="mt-1 text-xs text-muted-foreground">{pickLocalized(s.niche, lang)}</p>
                </div>
                <span className="dateline flex-none border border-rule px-1.5 py-0.5 text-[10px]">{s.lang.toUpperCase()}</span>
              </div>
              <div className="flex items-center justify-between border-t border-rule px-4 py-3 text-xs">
                <span className="text-muted-foreground">
                  {t("marketing.home.sites.articles", { count: s.articles, formatted: s.articles.toLocaleString(locale) })}
                </span>
                <a
                  href={siteUrl(s)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 font-medium text-foreground hover:text-accent-press"
                >
                  {t("marketing.home.sites.visit")} <ExternalLink className="h-3 w-3" />
                </a>
              </div>
            </article>
          ))}
        </div>
      </Section>

      <Rule />

      {/* E — Choose your AI model per step */}
      <Section padding="md">
        <div className="grid grid-cols-1 gap-12 lg:grid-cols-2">
          <div>
            <Eyebrow>{t("marketing.home.aiChoice.eyebrow")}</Eyebrow>
            <DisplayHeading level={2} className="mt-4">{t("marketing.home.aiChoice.title")}</DisplayHeading>
            <p className="mt-5 text-base leading-relaxed text-muted-foreground">{t("marketing.home.aiChoice.p1")}</p>
            <p className="mt-4 text-base leading-relaxed text-muted-foreground">{t("marketing.home.aiChoice.p2")}</p>
            <Link to="/fonctionnalites#modeles-ia" className="mt-6 inline-flex items-center gap-2 text-sm font-medium text-foreground hover:text-accent-press">
              {t("marketing.home.aiChoice.cta")} <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
          <div className="border border-rule bg-background">
            <table className="w-full text-left text-sm">
              <thead>
                <tr className="border-b border-rule bg-paper">
                  <th className="px-4 py-3 dateline">{t("marketing.home.aiChoice.table.task")}</th>
                  <th className="px-4 py-3 dateline">{t("marketing.home.aiChoice.table.model")}</th>
                  <th className="px-4 py-3 dateline text-right">{t("marketing.home.aiChoice.table.credits")}</th>
                </tr>
              </thead>
              <tbody>
                {[
                  { k: "rewriteFast", credits: 2, tag: "Recommandé" },
                  { k: "rewriteDeep", credits: 6, tag: "Premium" },
                  { k: "image", credits: 1, tag: "Recommandé" },
                  { k: "imageUltra", credits: 4, tag: "Premium" },
                  { k: "qa", credits: 2, tag: "" },
                  { k: "seo", credits: 1, tag: "" },
                ].map((row) => (
                  <tr key={row.k} className="border-b border-rule last:border-b-0">
                    <td className="px-4 py-3 text-foreground">{t(`marketing.home.aiChoice.table.rows.${row.k}.task`)}</td>
                    <td className="px-4 py-3 text-muted-foreground">
                      {t(`marketing.home.aiChoice.table.rows.${row.k}.model`)}
                      {row.tag && (
                        <span className={cn(
                          "ml-2 inline-block px-2 py-0.5 text-[10px] uppercase tracking-wider",
                          row.tag === "Premium" ? "border border-accent-press text-accent-press" : "bg-paper text-muted-foreground",
                        )}>
                          {row.tag === "Premium" ? t("marketing.home.aiChoice.tagPremium") : t("marketing.home.aiChoice.tagRecommended")}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right text-foreground tabular-nums">{row.credits}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </Section>

      <Rule />

      {/* F — Who it is for */}
      <Section padding="md" tone="paper">
        <div className="max-w-2xl">
          <Eyebrow>{t("marketing.home.personas.eyebrow")}</Eyebrow>
          <DisplayHeading level={2} className="mt-4">{t("marketing.home.personas.title")}</DisplayHeading>
        </div>
        <div className="mt-10">
          <FeatureTriad
            items={[
              {
                icon: Newspaper,
                title: <Link to="/pour/editeurs-solo" className="hover:text-accent-press">{t("marketing.home.personas.solo.title")}</Link>,
                body: t("marketing.home.personas.solo.body"),
                footnote: <Link to="/pour/editeurs-solo" className="hover:underline">{t("marketing.home.personas.seeMore")}</Link>,
              },
              {
                icon: Workflow,
                title: <Link to="/pour/agences-medias" className="hover:text-accent-press">{t("marketing.home.personas.agencies.title")}</Link>,
                body: t("marketing.home.personas.agencies.body"),
                footnote: <Link to="/pour/agences-medias" className="hover:underline">{t("marketing.home.personas.seeMore")}</Link>,
              },
              {
                icon: Layers,
                title: <Link to="/pour/groupes-presse" className="hover:text-accent-press">{t("marketing.home.personas.groups.title")}</Link>,
                body: t("marketing.home.personas.groups.body"),
                footnote: <Link to="/pour/groupes-presse" className="hover:underline">{t("marketing.home.personas.seeMore")}</Link>,
              },
            ]}
          />
        </div>
      </Section>

      <Rule />

      {/* G — Pricing teaser: same plans and prices as /tarifs. */}
      <Section padding="md">
        <div className="flex flex-col items-start gap-6 md:flex-row md:items-end md:justify-between">
          <div className="max-w-xl">
            <Eyebrow>{t("marketing.home.pricing.eyebrow")}</Eyebrow>
            <DisplayHeading level={2} className="mt-4">{t("marketing.home.pricing.title")}</DisplayHeading>
          </div>
          <Link to="/tarifs" className="inline-flex items-center gap-2 text-sm font-medium text-foreground hover:text-accent-press">
            {t("marketing.home.pricing.seeAll")} <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
        <div className="mt-10 grid grid-cols-1 gap-4 md:grid-cols-3">
          {TEASER_PLANS.map((slug) => {
            const popular = slug === POPULAR_PLAN;
            const features = (t(`marketing.pages.tarifs.plans.features.${slug}`, { returnObjects: true }) as string[]).slice(0, 4);
            const setup = planSetupCents(slug) ?? 0;
            return (
              <article
                key={slug}
                className={cn(
                  "lift-card relative flex flex-col gap-5 border bg-background p-7 md:p-8",
                  popular ? "border-accent-press shadow-[0_24px_60px_-24px_hsl(var(--accent-press)/0.4)]" : "border-rule",
                )}
              >
                <div className="relative flex items-start justify-between">
                  <h3 className="text-display text-2xl leading-tight">{locText(PLAN_DISPLAY_NAMES[slug], lang)}</h3>
                  {popular && (
                    <span className="inline-block bg-accent-press px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-accent-press-foreground">
                      {t("marketing.home.pricing.recommended")}
                    </span>
                  )}
                </div>
                <div className="relative flex items-baseline gap-1">
                  <span className="text-display text-4xl text-foreground">{formatMoney(planMonthlyCents(slug) ?? 0, lang)}</span>
                  <span className="dateline">{t("marketing.home.pricing.perMonth")}</span>
                </div>
                <p className="relative text-sm text-muted-foreground">{locText(PLAN_AUDIENCE[slug], lang)}</p>
                {setup > 0 && (
                  <p className="relative -mt-3 text-xs text-muted-foreground">
                    {t("marketing.pages.tarifs.plans.setupFee", { amount: formatMoney(setup, lang) })}
                  </p>
                )}
                <ul className="relative mt-2 space-y-2 text-sm">
                  {features.map((f, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <span aria-hidden className="mt-2 inline-block h-1 w-3 flex-none bg-accent-press" />
                      <span className="text-foreground">{f}</span>
                    </li>
                  ))}
                </ul>
                <Link
                  to={`/tarifs#${slug}`}
                  className="relative mt-auto inline-flex h-11 items-center justify-center bg-foreground px-4 text-xs font-semibold uppercase tracking-wider text-background transition-all hover:bg-accent-press hover:text-accent-press-foreground hover:shadow-[0_16px_40px_-16px_hsl(var(--accent-press)/0.6)]"
                >
                  {t("marketing.home.pricing.choose")}
                </Link>
              </article>
            );
          })}
        </div>
      </Section>

      <Rule />

      {/* H — Resources teaser: real published guides. */}
      <Section padding="md" tone="paper">
        <div className="flex flex-col items-start gap-6 md:flex-row md:items-end md:justify-between">
          <div className="max-w-2xl">
            <Eyebrow>{t("marketing.home.resources.eyebrow")}</Eyebrow>
            <DisplayHeading level={2} className="mt-4">{t("marketing.home.resources.title")}</DisplayHeading>
          </div>
          <Link to="/ressources" className="inline-flex items-center gap-2 text-sm font-medium text-foreground hover:text-accent-press">
            {t("marketing.home.resources.seeAll")} <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
        <div className="mt-10 grid grid-cols-1 gap-4 md:grid-cols-3">
          {resources.slice(0, 3).map((r) => {
            const title = pickDbText(r, "title", lang) || r.slug;
            return (
              <article key={r.slug} className="lift-card group relative overflow-hidden border border-rule bg-background p-7 md:p-8">
                <div
                  aria-hidden
                  className="absolute inset-x-0 top-0 h-1 origin-left scale-x-0 bg-accent-press transition-transform duration-500 group-hover:scale-x-100"
                />
                <span className="dateline">{categoryLabel(r.category)}</span>
                <h3 className="mt-3 text-display text-xl leading-tight">
                  <Link to={`/ressources/${r.slug}`} className="hover:text-accent-press">{title}</Link>
                </h3>
                {r.reading_minutes ? (
                  <p className="mt-4 text-xs text-muted-foreground">{t("marketing.home.resources.readingTime", { min: r.reading_minutes })}</p>
                ) : null}
              </article>
            );
          })}
        </div>
      </Section>

      {/* I — Final CTA */}
      <CTABanner
        tone="accent"
        headline={t("marketing.home.finalCta.title")}
        subhead={t("marketing.home.finalCta.subhead")}
        primary={{ label: t("marketing.home.finalCta.primary"), to: "/dpadmin?intent=signup" }}
        secondary={{ label: t("marketing.home.finalCta.secondary"), to: "/contact" }}
      />
    </>
  );
}
